import { theme } from "@zesty-io/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, InputLabel, TextField, InputAdornment, IconButton, Typography, Tooltip, Alert, Button } from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckIcon from "@mui/icons-material/Check";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import { ContentModel } from "../../../../../shell/services/types";
import { useGetWebViewsQuery, useUpdateContentModelMutation } from "../../../../../shell/services/instance";
import { SelectModelParentInput } from "./SelectModelParentInput";
import { useHistory } from "react-router";
interface Props {
  model: ContentModel;
}
export const FieldsListRight = ({
  model
}: Props) => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const [description, setDescription] = useState("");
  const [isCopied, setIsCopied] = useState(null);
  const [newParentZUID, setNewParentZUID] = useState(null);
  const [showSaveParentModelButton, setshowSaveParentModelButton] = useState(false);
  const {
    data: views
  } = useGetWebViewsQuery();
  const view = views?.find(view => view?.contentModelZUID === model?.ZUID);
  useEffect(() => {
    if (model?.parentZUID) {
      setNewParentZUID(model.parentZUID === "0" ? null : model.parentZUID);
    }
  }, [model]);
  const [updateContentModel, {
    isLoading,
    isSuccess
  }] = useUpdateContentModelMutation();
  useEffect(() => {
    if (model?.description) {
      setDescription(model?.description || "");
    }
  }, [model]);
  useEffect(() => {
    if (isSuccess) {
      setshowSaveParentModelButton(false);
    }
  }, [isSuccess]);
  const handleCopyClick = (data: string) => {
    navigator?.clipboard?.writeText(data).then(() => {
      setIsCopied(data);
      setTimeout(() => {
        setIsCopied(null);
      }, 3000);
    }).catch(err => {
      console.error(err);
    });
  };
  const handleSave = (type: "description" | "parentZUID" | "blockGroup") => {
    let body = {
      ...model
    };
    switch (type) {
      case "description":
        body = {
          ...body,
          description
        };
        break;
      case "parentZUID":
        body = {
          ...body,
          parentZUID: newParentZUID ?? "0"
        };
        break;
      case "blockGroup":
        // TODO: Update data here
        break;
      default:
        break;
    }
    updateContentModel({
      ZUID: model.ZUID,
      body
    });
  };
  return <Box height="100%" width="280px" bgcolor="grey.50" py={2} pl={2} pr={4} data-sentry-element="Box" data-sentry-component="FieldsListRight" data-sentry-source-file="FieldsListRight.tsx">
      {model?.type === "block" && view?.version === 1 && <Alert severity="warning" sx={{
      display: "block",
      mb: 3
    }}>
          <Box>
            <Typography fontWeight={700} color="warning.dark">
              {t("schema.templateFileEmpty")}
            </Typography>
            <Typography variant="body2">
              {t("schema.templateFileEmptyBody")}
            </Typography>
          </Box>
          <Button color="warning" variant="contained" startIcon={<CodeRoundedIcon />} sx={{
        mt: 1.5
      }} onClick={() => history.push(`/code/file/views/${view?.ZUID}`)}>
            {t("schema.editTemplateFile")}
          </Button>
        </Alert>}
      <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="FieldsListRight.tsx">
        {t("schema.referenceId")}
        <Tooltip placement="top" title={t("schema.referenceIdTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FieldsListRight.tsx">
          <InfoRoundedIcon sx={{
          ml: 1,
          width: "12px",
          height: "12px"
        }} color="action" data-sentry-element="InfoRoundedIcon" data-sentry-source-file="FieldsListRight.tsx" />
        </Tooltip>
      </InputLabel>
      <TextField disabled value={model?.name} fullWidth inputProps={{
      sx: {
        ":read-only": {
          textFillColor: theme.palette.text.primary
        }
      }
    }} InputProps={{
      endAdornment: <InputAdornment position="end">
              <IconButton size="small" onClick={() => handleCopyClick(model?.name)}>
                {isCopied === model?.name ? <CheckIcon color="action" /> : <ContentCopyRoundedIcon color="action" />}
              </IconButton>
            </InputAdornment>
    }} data-sentry-element="TextField" data-sentry-source-file="FieldsListRight.tsx" />
      <InputLabel sx={{
      mt: 3
    }} data-sentry-element="InputLabel" data-sentry-source-file="FieldsListRight.tsx">
        {t("schema.zuidLabel")}
        <Tooltip placement="top" title={t("schema.zuidTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FieldsListRight.tsx">
          <InfoRoundedIcon sx={{
          ml: 1,
          width: "12px",
          height: "12px"
        }} color="action" data-sentry-element="InfoRoundedIcon" data-sentry-source-file="FieldsListRight.tsx" />
        </Tooltip>
      </InputLabel>
      <TextField disabled value={model?.ZUID} fullWidth inputProps={{
      sx: {
        ":read-only": {
          textFillColor: theme.palette.text.primary
        }
      }
    }} InputProps={{
      endAdornment: <InputAdornment position="end">
              <IconButton size="small" onClick={() => handleCopyClick(model?.ZUID)}>
                {isCopied === model?.ZUID ? <CheckIcon color="action" /> : <ContentCopyRoundedIcon color="action" />}
              </IconButton>
            </InputAdornment>
    }} data-sentry-element="TextField" data-sentry-source-file="FieldsListRight.tsx" />
      <Box mt={3} data-sentry-element="Box" data-sentry-source-file="FieldsListRight.tsx">
        <SelectModelParentInput modelType={model?.type} value={newParentZUID} onChange={value => {
        setshowSaveParentModelButton(value !== model?.parentZUID);
        setNewParentZUID(value);
      }} label={t("schema.modelParent")} data-sentry-element="SelectModelParentInput" data-sentry-source-file="FieldsListRight.tsx" />
        {showSaveParentModelButton && <Button color="primary" loading={isLoading} variant="contained" onClick={() => handleSave("parentZUID")} sx={{
        mt: 1.5
      }}>
            {t("common.save")}
          </Button>}
      </Box>

      {/* Block grouping will be implemented at a different point  */}
      {/* {model?.type === "block" && (
        <Box mt={3}>
          <InputLabel>
            Block Group
            <Tooltip
              placement="top"
              title="Add your block model to an existing group"
            >
              <InfoRoundedIcon
                sx={{ ml: 1, width: "12px", height: "12px" }}
                color="action"
              />
            </Tooltip>
          </InputLabel>
          <Autocomplete
            options={[]}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select" />
            )}
          />
          <Button
            color="primary"
            loading={isLoading}
            variant="contained"
            onClick={() => handleSave("blockGroup")}
            sx={{ mt: 1.5 }}
          >
            Save
          </Button>
        </Box>
       )} */}

      <InputLabel sx={{
      mt: 3
    }} data-sentry-element="InputLabel" data-sentry-source-file="FieldsListRight.tsx">
        {t("schema.descriptionLabel")}
        <Tooltip placement="top" title={t("schema.descriptionTooltip")} data-sentry-element="Tooltip" data-sentry-source-file="FieldsListRight.tsx">
          <InfoRoundedIcon sx={{
          ml: 1,
          width: "12px",
          height: "12px"
        }} color="action" data-sentry-element="InfoRoundedIcon" data-sentry-source-file="FieldsListRight.tsx" />
        </Tooltip>
      </InputLabel>
      <TextField value={description} onChange={event => setDescription(event.target.value)} fullWidth multiline minRows={4} data-sentry-element="TextField" data-sentry-source-file="FieldsListRight.tsx" />
      {description !== model?.description && <Button color="primary" loading={isLoading} startIcon={<SaveRoundedIcon />} loadingPosition="start" variant="contained" onClick={() => handleSave("description")} sx={{
      mt: 1.5
    }}>
          {t("common.save")}
        </Button>}
      {/* <Box
        sx={{
          mt: 3,
          mb: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
        }}
       >
        <Typography variant="overline">DOCUMENTATION</Typography>
       </Box>
       <Box
        component="ul"
        sx={{
          color: "info.dark",
          lineHeight: "20px",
          listStylePosition: "inside",
        }}
        fontSize={14}
       >
        <Box component="li" pb={0.5}>
          <Link href="#">How to create models</Link>
        </Box>
        <Box component="li" pb={0.5}>
          <Link href="#">Fields in Schema</Link>
        </Box>
        <Box component="li" pb={0.5}>
          <Link href="#">Accessing your Model's Data</Link>
        </Box>
        <Box component="li" pb={0.5}>
          <Link href="#">Zesty APIs</Link>
        </Box>
        <Box component="li">
          <Link href="#">De-activating Fields</Link>
        </Box>
       </Box> */}
    </Box>;
};